import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type { CreateVirtualPersonBody, PeopleOutput } from '@/types'
import { createVirtualPerson, getJobRoles, getPeople } from '../services/people'

/**
 * Fetch all people, optionally filtered by CR.
 * @param all  When provided, only people belonging to that CR are returned.
 * @param crId  Explicit CR id to filter by, overriding the profile's default CR
 *              and the `all` flag. Useful when the CR comes from a specific
 *              record being edited rather than from the current context.
 */
export function usePeople(all?: boolean, crId?: string) {
	const { defaultContractId } = useProfileStore()

	const effectiveCrId = crId ?? (!all ? defaultContractId : undefined)
	return useQuery<PeopleOutput, Error, PeopleOutput['items']>({
		queryKey: ['people', effectiveCrId ?? 'all'],
		queryFn: () => getPeople(effectiveCrId),
		staleTime: 5 * 60 * 1000, // 5 minutos
		select: (data) => data.items,
	})
}

/** Cargos distintos do ERP, usados pra montar o combobox de Grupo de Cargo. */
export function useJobRoles(q?: string) {
	return useQuery({
		queryKey: ['job-roles', q ?? ''],
		queryFn: () => getJobRoles(q),
		staleTime: 10 * 60 * 1000,
		select: (data) => data.items,
	})
}

export function useCreateVirtualPerson() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateVirtualPersonBody) => createVirtualPerson(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['people'] })
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
	})
}
